package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.StoreAppChoiceDto
import dev.lingvo.shared.StoreCapabilityStatus
import dev.lingvo.shared.StoreConnectionCheckDto
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.allowStoreConnectionApps
import dev.lingvo.site.api.checkStoreConnection
import dev.lingvo.site.api.storeConnectionApps
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

@Composable
internal fun StoreAppleWizard(
    connection: StoreConnectionDto,
    orgSlug: String,
    runner: StoreActionRunner,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    clearError: () -> Unit,
    onError: (Throwable) -> Boolean,
    onAllowed: (StoreConnectionDto) -> Unit,
    onBind: (String, String?) -> Unit,
) {
    var apps by remember(connection.id) { mutableStateOf<List<StoreAppChoiceDto>?>(null) }
    var ref by remember(connection.id) { mutableStateOf("") }
    var check by remember(connection.id) { mutableStateOf<StoreConnectionCheckDto?>(null) }
    fun load() {
        clearError()
        runner.run(setBusy, tr("app.projects.slug.store_apple_wizard.couldnt_load_the_apps_try_again", "Couldn't load the apps. Try again."),
            transport = { ApiClient.storeConnectionApps(orgSlug, connection.id) }, onError = onError) { apps = it }
    }
    LaunchedEffect(connection.id) { load() }
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        LMuted(t("app.projects.slug.store_apple_wizard.choose_the_app_lingvo_may_use", "Choose the app Lingvo may use. Apps outside this connection's allowed list are refused by the server."))
        if (apps.isNullOrEmpty()) {
            LAlert(
                if (apps == null) t("app.projects.slug.store_apple_wizard.waiting_for_your_app_list", "Waiting for your app list") else t("app.projects.slug.store_apple_wizard.no_apps_are_visible_to_this", "No apps are visible to this key"),
                t("app.projects.slug.store_apple_wizard.for_an_individual_key_give_its", "For an individual key, give its user access to the app in Users and Access. Then reload this list."),
                variant = LAlertVariant.INFO, live = apps != null,
            )
            LButton(t("app.projects.slug.store_apple_wizard.reload_apps", "Reload apps"), onClick = ::load, enabled = !busy, loading = busy, variant = LButtonVariant.SECONDARY)
        } else {
            LSelect(ref, listOf(LSelectOption("", t("app.projects.slug.store_apple_wizard.choose_an_app", "Choose an app"))) + apps.orEmpty().map {
                LSelectOption(it.storeAppRef, "${it.displayName} (${it.bundleId ?: it.storeAppRef})")
            }, { ref = it; check = null; clearError() }, modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0"); property("max-width", "100%") }, label = t("app.projects.slug.store_apple_wizard.app", "App"), enabled = !busy)
            LMuted(t("app.projects.slug.store_apple_wizard.checking_listing_access_sends_the_apps", "Checking listing access sends the app's current name back unchanged. The screenshot check reads its asset library."))
            LButton(t("app.projects.slug.store_apple_wizard.check_feature_access", "Check feature access"), onClick = {
                clearError()
                runner.run(setBusy, tr("app.projects.slug.store_apple_wizard.couldnt_check_the_apps_permissions_try", "Couldn't check the app's permissions. Try again."),
                    transport = { ApiClient.checkStoreConnection(orgSlug, connection.id, ref) }, onError = onError) { check = it }
            }, enabled = ref.isNotBlank() && !busy, loading = busy, variant = LButtonVariant.SECONDARY)
            check?.takeIf { it.storeAppRef == ref }?.let { result ->
                StoreAppleCapabilities(result)
                LButton(t("app.projects.slug.store_apple_wizard.allow_this_app_and_connect", "Allow this app and connect"), onClick = {
                    clearError()
                    runner.run(setBusy, tr("app.projects.slug.store_apple_wizard.couldnt_save_the_allowed_app_try", "Couldn't save the allowed app. Try again."),
                        transport = { ApiClient.allowStoreConnectionApps(orgSlug, connection.id, (connection.allowedAppRefs + ref).distinct()) },
                        onError = onError,
                    ) {
                        // The second runner admission checks authority again between the two server calls.
                        onAllowed(it)
                        onBind(ref, apps.orEmpty().firstOrNull { app -> app.storeAppRef == ref }?.displayName)
                    }
                }, enabled = !busy && result.listing != StoreCapabilityStatus.ROLE_BLOCKED, loading = busy)
            }
        }
    }
}

@Composable
internal fun StoreAppleCapabilities(check: StoreConnectionCheckDto) {
    val listingOk = check.listing == StoreCapabilityStatus.VERIFIED
    val listingBlocked = check.listing == StoreCapabilityStatus.ROLE_BLOCKED
    LAlert(
        if (listingOk) t("app.projects.slug.store_apple_wizard.listing_text_permission_verified", "Listing text: permission verified")
        else if (listingBlocked) t("app.projects.slug.store_apple_wizard.listing_text_is_blocked_by_this", "Listing text is blocked by this key's role")
        else t("app.projects.slug.store_apple_wizard.listing_write_permission_could_not_be", "Listing write permission could not be checked yet"),
        if (listingOk) t("app.projects.slug.store_apple_wizard.the_store_accepted_the_unchanged_name", "The store accepted the unchanged name.")
        else if (listingBlocked) t("app.projects.slug.store_apple_wizard.use_marketing_app_manager_admin_or", "Use Marketing, App Manager, Admin or Account Holder to edit listing text.")
        else t("app.projects.slug.store_apple_wizard.create_an_editable_version_with_a", "Create an editable version with a name in App Store Connect, then check again. You can connect now to import the listing."),
        variant = if (listingOk) LAlertVariant.SUCCESS else LAlertVariant.WARNING, live = true,
    )
    LAlert(
        if (check.screenshots == StoreCapabilityStatus.VERIFIED) t("app.projects.slug.store_apple_wizard.screenshot_asset_library_access_verified", "Screenshot asset library: access verified")
        else t("app.projects.slug.store_apple_wizard.screenshot_asset_library_access_not_verified", "Screenshot asset library: access not verified"),
        if (check.screenshots == StoreCapabilityStatus.ROLE_BLOCKED) t("app.projects.slug.store_apple_wizard.screenshots_need_app_manager_or_higher", "Screenshots need App Manager or higher. You can keep using listing text with Marketing.")
        else if (check.screenshots == StoreCapabilityStatus.VERIFIED) t("app.projects.slug.store_apple_wizard.the_key_can_read_this_apps", "The key can read this app's asset library.")
        else t("app.projects.slug.store_apple_wizard.the_library_is_unavailable_right_now", "The library is unavailable right now. Check the app's state and try again before working with screenshots."),
        variant = if (check.screenshots == StoreCapabilityStatus.VERIFIED) LAlertVariant.SUCCESS else LAlertVariant.INFO, live = true,
    )
}
